import React, {useState, useRef, useEffect, useCallback} from 'react';
import {createPortal} from 'react-dom';
import {Car, ContextMenuGroup, ContextMenuItem, ContextActionKey, ContextMenuPosition} from '../../types';

interface PrintBLOptions {
    isOriginal: boolean;
    includeRelated: boolean;
}

interface PrintBookingConfirmationOptions {
    includeRelated: boolean;
}

interface ContextMenuProps {
    car: Car;
    position: ContextMenuPosition;
    groups: ContextMenuGroup[];
    onAction: (action: ContextActionKey, car: Car) => void;
    onClose: () => void;
    onPrintBL?: (car: Car, options: PrintBLOptions) => void;
    onPrintBookingConfirmation?: (car: Car, options: PrintBookingConfirmationOptions) => void;
}

/**
 * Icon components for context menu items
 */
const Icons = {
    focus: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
        </svg>
    ),
    edit: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
        </svg>
    ),
    change: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
        </svg>
    ),
    set: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
    ),
    print: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
        </svg>
    ),
    export: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
    ),
    delete: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
        </svg>
    ),
    insurance: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
        </svg>
    ),
    chevronRight: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
        </svg>
    ),
    loaded: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M5 13l4 4L19 7"/>
        </svg>
    ),
    voyage: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
        </svg>
    ),
    consignee: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
        </svg>
    ),
    destination: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
    ),
    pricing: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
    ),
};

/**
 * Get icon for an action key
 */
const getIconForAction = (key: ContextActionKey): React.ReactNode => {
    switch (key) {
        case 'focus_ref':
        case 'focus_chassis':
        case 'focus_shipper':
        case 'focus_consignee':
            return Icons.focus;
        case 'edit':
            return Icons.edit;
        case 'change_voyage':
            return Icons.voyage;
        case 'change_consignee':
            return Icons.consignee;
        case 'change_destination':
            return Icons.destination;
        case 'mark_loaded':
        case 'mark_unloaded':
            return Icons.loaded;
        case 'set_pricing':
        case 'set_length_pricing':
            return Icons.pricing;
        case 'add_insurance':
        case 'remove_insurance':
            return Icons.insurance;
        case 'print_bill_of_lading':
        case 'print_booking_confirmation':
            return Icons.print;
        case 'export_excel':
            return Icons.export;
        case 'delete':
            return Icons.delete;
        default:
            return null;
    }
};

/**
 * Individual item within a submenu - supports PrintBL and PrintBookingConfirmation interactive submenus
 */
const SubMenuItem: React.FC<{
    item: ContextMenuItem;
    car: Car;
    onAction: (action: ContextActionKey) => void;
    onPrintBL?: (options: PrintBLOptions) => void;
    onPrintBookingConfirmation?: (options: PrintBookingConfirmationOptions) => void;
}> = ({item, car, onAction, onPrintBL, onPrintBookingConfirmation}) => {
    const [showSubmenu, setShowSubmenu] = useState(false);
    const itemRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const isDisabled = typeof item.disabled === 'function'
        ? item.disabled(car)
        : item.disabled;

    // Check if this is an item that should show an interactive submenu
    const hasPrintBLSubmenu = item.key === 'print_bill_of_lading' && !!onPrintBL;
    const hasPrintBookingConfirmationSubmenu = item.key === 'print_booking_confirmation' && !!onPrintBookingConfirmation;
    const hasInteractiveSubmenu = hasPrintBLSubmenu || hasPrintBookingConfirmationSubmenu;

    const handleMouseEnter = useCallback(() => {
        if (hasInteractiveSubmenu) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            setShowSubmenu(true);
        }
    }, [hasInteractiveSubmenu]);

    const handleMouseLeave = useCallback(() => {
        if (hasInteractiveSubmenu) {
            timeoutRef.current = setTimeout(() => {
                setShowSubmenu(false);
            }, 150);
        }
    }, [hasInteractiveSubmenu]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <div
            ref={itemRef}
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <button
                onClick={() => !hasInteractiveSubmenu && !isDisabled && onAction(item.key)}
                disabled={isDisabled && !hasInteractiveSubmenu}
                className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors ${
                    isDisabled
                        ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : item.danger
                            ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 cursor-pointer'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
                }`}
            >
                {item.icon || getIconForAction(item.key)}
                <span className="flex-1">{item.label}</span>
                {hasInteractiveSubmenu && (
                    <span className="text-gray-400">
                        {Icons.chevronRight}
                    </span>
                )}
            </button>

            {hasPrintBLSubmenu && showSubmenu && (
                <PrintBLSubmenu
                    car={car}
                    onPrint={onPrintBL!}
                    parentRef={itemRef}
                />
            )}

            {hasPrintBookingConfirmationSubmenu && showSubmenu && (
                <PrintBookingConfirmationSubmenu
                    car={car}
                    onPrint={onPrintBookingConfirmation!}
                    parentRef={itemRef}
                />
            )}
        </div>
    );
};

/**
 * Nested submenu component - renders MenuItem components to support further nesting
 */
const SubMenu: React.FC<{
    items: ContextMenuItem[];
    car: Car;
    onAction: (action: ContextActionKey) => void;
    parentRef: React.RefObject<HTMLDivElement | null>;
    onPrintBL?: (options: PrintBLOptions) => void;
    onPrintBookingConfirmation?: (options: PrintBookingConfirmationOptions) => void;
}> = ({items, car, onAction, parentRef, onPrintBL, onPrintBookingConfirmation}) => {
    const submenuRef = useRef<HTMLDivElement>(null);
    const [horizontalPosition, setHorizontalPosition] = useState<'right' | 'left'>('right');
    const [verticalOffset, setVerticalOffset] = useState(0);

    useEffect(() => {
        if (submenuRef.current && parentRef.current) {
            const submenuRect = submenuRef.current.getBoundingClientRect();
            const parentRect = parentRef.current.getBoundingClientRect();

            // Check if submenu would overflow right edge
            if (parentRect.right + submenuRect.width > window.innerWidth) {
                setHorizontalPosition('left');
            }

            // Check if submenu would overflow bottom edge
            const bottomOverflow = parentRect.top + submenuRect.height - window.innerHeight + 10;
            if (bottomOverflow > 0) {
                setVerticalOffset(-bottomOverflow);
            }
        }
    }, [parentRef]);

    return (
        <div
            ref={submenuRef}
            className={`absolute z-[10000] min-w-[200px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg py-1 ${
                horizontalPosition === 'right' ? 'left-full ml-1' : 'right-full mr-1'
            }`}
            style={{top: verticalOffset}}
        >
            {items.map((item) => (
                <SubMenuItem
                    key={item.key}
                    item={item}
                    car={car}
                    onAction={onAction}
                    onPrintBL={onPrintBL}
                    onPrintBookingConfirmation={onPrintBookingConfirmation}
                />
            ))}
        </div>
    );
};

/**
 * Interactive Print Booking Confirmation submenu with checkbox and print button
 */
const PrintBookingConfirmationSubmenu: React.FC<{
    car: Car;
    onPrint: (options: PrintBookingConfirmationOptions) => void;
    parentRef: React.RefObject<HTMLDivElement | null>;
}> = ({car, onPrint, parentRef}) => {
    const submenuRef = useRef<HTMLDivElement>(null);
    const [horizontalPosition, setHorizontalPosition] = useState<'right' | 'left'>('right');
    const [verticalOffset, setVerticalOffset] = useState(0);
    const [includeRelated, setIncludeRelated] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        if (submenuRef.current && parentRef.current) {
            const submenuRect = submenuRef.current.getBoundingClientRect();
            const parentRect = parentRef.current.getBoundingClientRect();

            if (parentRect.right + submenuRect.width > window.innerWidth) {
                setHorizontalPosition('left');
            }

            const bottomOverflow = parentRect.top + submenuRect.height - window.innerHeight + 10;
            if (bottomOverflow > 0) {
                setVerticalOffset(-bottomOverflow);
            }
        }
    }, [parentRef]);

    const handlePrint = async () => {
        setIsGenerating(true);
        try {
            await onPrint({includeRelated});
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div
            ref={submenuRef}
            className={`absolute z-[10000] w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg ${
                horizontalPosition === 'right' ? 'left-full ml-1' : 'right-full mr-1'
            }`}
            style={{top: verticalOffset}}
        >
            <div className="py-2">
                <div className="px-3 py-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={includeRelated}
                            onChange={(e) => setIncludeRelated(e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Print Related Units</span>
                    </label>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                    <button
                        onClick={handlePrint}
                        disabled={isGenerating}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 cursor-pointer"
                    >
                        {isGenerating ? 'Generating...' : 'Print Booking Confirmation'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Interactive Print BL submenu with radio buttons, checkbox, and print button
 */
const PrintBLSubmenu: React.FC<{
    car: Car;
    onPrint: (options: PrintBLOptions) => void;
    parentRef: React.RefObject<HTMLDivElement | null>;
}> = ({car, onPrint, parentRef}) => {
    const submenuRef = useRef<HTMLDivElement>(null);
    const [horizontalPosition, setHorizontalPosition] = useState<'right' | 'left'>('right');
    const [verticalOffset, setVerticalOffset] = useState(0);
    const [isOriginal, setIsOriginal] = useState(false);
    const [includeRelated, setIncludeRelated] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        if (submenuRef.current && parentRef.current) {
            const submenuRect = submenuRef.current.getBoundingClientRect();
            const parentRect = parentRef.current.getBoundingClientRect();

            // Check if submenu would overflow right edge
            if (parentRect.right + submenuRect.width > window.innerWidth) {
                setHorizontalPosition('left');
            }

            // Check if submenu would overflow bottom edge
            const bottomOverflow = parentRect.top + submenuRect.height - window.innerHeight + 10;
            if (bottomOverflow > 0) {
                setVerticalOffset(-bottomOverflow);
            }
        }
    }, [parentRef]);

    const handlePrint = async () => {
        setIsGenerating(true);
        try {
            await onPrint({isOriginal, includeRelated});
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div
            ref={submenuRef}
            className={`absolute z-[10000] w-52 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg ${
                horizontalPosition === 'right' ? 'left-full ml-1' : 'right-full mr-1'
            }`}
            style={{top: verticalOffset}}
        >
            <div className="py-2">
                <div className="px-3 pb-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    BL Type
                </div>
                <div className="px-3 py-2 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name={`blType-${car.id}`}
                            checked={isOriginal}
                            onChange={() => setIsOriginal(true)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Original</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name={`blType-${car.id}`}
                            checked={!isOriginal}
                            onChange={() => setIsOriginal(false)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Copy</span>
                    </label>
                </div>
                <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={includeRelated}
                            onChange={(e) => setIncludeRelated(e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Include Related</span>
                    </label>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                    <button
                        onClick={handlePrint}
                        disabled={isGenerating}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 cursor-pointer"
                    >
                        {isGenerating ? 'Generating...' : 'Print Bill of Lading'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Menu item component that supports nested menus
 */
const MenuItem: React.FC<{
    item: ContextMenuItem;
    car: Car;
    onAction: (action: ContextActionKey) => void;
    onPrintBL?: (options: PrintBLOptions) => void;
    onPrintBookingConfirmation?: (options: PrintBookingConfirmationOptions) => void;
}> = ({item, car, onAction, onPrintBL, onPrintBookingConfirmation}) => {
    const [showSubmenu, setShowSubmenu] = useState(false);
    const itemRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Check if this item should show a submenu (either regular items or special interactive submenu)
    const hasRegularSubmenu = item.items && item.items.length > 0;
    const hasPrintBLSubmenu = item.key === 'print_bill_of_lading' && !!onPrintBL;
    const hasPrintBookingConfirmationSubmenu = item.key === 'print_booking_confirmation' && !!onPrintBookingConfirmation;
    const hasAnySubmenu = hasRegularSubmenu || hasPrintBLSubmenu || hasPrintBookingConfirmationSubmenu;

    const handleMouseEnter = useCallback(() => {
        if (hasAnySubmenu) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            setShowSubmenu(true);
        }
    }, [hasAnySubmenu]);

    const handleMouseLeave = useCallback(() => {
        if (hasAnySubmenu) {
            timeoutRef.current = setTimeout(() => {
                setShowSubmenu(false);
            }, 150);
        }
    }, [hasAnySubmenu]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const isDisabled = typeof item.disabled === 'function'
        ? item.disabled(car)
        : item.disabled;

    return (
        <div
            ref={itemRef}
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <button
                onClick={() => !hasAnySubmenu && !isDisabled && onAction(item.key)}
                disabled={isDisabled && !hasAnySubmenu}
                className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors ${
                    isDisabled
                        ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : item.danger
                            ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 cursor-pointer'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
                }`}
            >
                {item.icon || getIconForAction(item.key)}
                <span className="flex-1">{item.label}</span>
                {hasAnySubmenu && (
                    <span className="text-gray-400">
                        {Icons.chevronRight}
                    </span>
                )}
            </button>

            {hasRegularSubmenu && showSubmenu && (
                <SubMenu
                    items={item.items!}
                    car={car}
                    onAction={onAction}
                    parentRef={itemRef}
                    onPrintBL={onPrintBL}
                    onPrintBookingConfirmation={onPrintBookingConfirmation}
                />
            )}

            {hasPrintBLSubmenu && !hasRegularSubmenu && showSubmenu && (
                <PrintBLSubmenu
                    car={car}
                    onPrint={onPrintBL!}
                    parentRef={itemRef}
                />
            )}

            {hasPrintBookingConfirmationSubmenu && !hasRegularSubmenu && showSubmenu && (
                <PrintBookingConfirmationSubmenu
                    car={car}
                    onPrint={onPrintBookingConfirmation!}
                    parentRef={itemRef}
                />
            )}
        </div>
    );
};

/**
 * Context menu component with nested menu support
 */
export const ContextMenu: React.FC<ContextMenuProps> = ({
                                                             car,
                                                             position,
                                                             groups,
                                                             onAction,
                                                             onClose,
                                                             onPrintBL,
                                                             onPrintBookingConfirmation,
                                                         }) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const [adjustedPosition, setAdjustedPosition] = useState(position);

    // Handle click outside, escape key, and scroll to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        const handleScroll = () => {
            onClose();
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        // Listen for scroll on capture phase to catch scrolling in any container
        document.addEventListener('scroll', handleScroll, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('scroll', handleScroll, true);
        };
    }, [onClose]);

    // Adjust position to keep menu on screen
    useEffect(() => {
        if (menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            let newX = position.x;
            let newY = position.y;

            // Check right edge
            if (position.x + rect.width > window.innerWidth - 10) {
                newX = window.innerWidth - rect.width - 10;
            }

            // Check bottom edge
            if (position.y + rect.height > window.innerHeight - 10) {
                newY = window.innerHeight - rect.height - 10;
            }

            // Check left edge
            if (newX < 10) {
                newX = 10;
            }

            // Check top edge
            if (newY < 10) {
                newY = 10;
            }

            if (newX !== position.x || newY !== position.y) {
                setAdjustedPosition({x: newX, y: newY});
            }
        }
    }, [position]);

    const handleAction = (action: ContextActionKey) => {
        onAction(action, car);
        onClose();
    };

    // Handle Print BL from interactive submenu
    const handlePrintBL = useCallback(async (options: PrintBLOptions) => {
        if (onPrintBL) {
            await onPrintBL(car, options);
            onClose();
        }
    }, [onPrintBL, car, onClose]);

    // Handle Print Booking Confirmation from interactive submenu
    const handlePrintBookingConfirmation = useCallback(async (options: PrintBookingConfirmationOptions) => {
        if (onPrintBookingConfirmation) {
            await onPrintBookingConfirmation(car, options);
            onClose();
        }
    }, [onPrintBookingConfirmation, car, onClose]);

    // Filter out empty groups
    const visibleGroups = groups.filter(group => group.items.length > 0);

    return createPortal(
        <div
            ref={menuRef}
            className="fixed z-[9999] min-w-[220px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl py-1"
            style={{top: adjustedPosition.y, left: adjustedPosition.x}}
        >
            {visibleGroups.map((group, groupIndex) => (
                <div key={group.title || `group-${groupIndex}`}>
                    {/* Group title - only show if not empty */}
                    {group.title && (
                        <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
                            {group.title}
                        </div>
                    )}

                    {/* Group items */}
                    {group.items.map((item) => (
                        <MenuItem
                            key={item.key}
                            item={item}
                            car={car}
                            onAction={handleAction}
                            onPrintBL={onPrintBL ? handlePrintBL : undefined}
                            onPrintBookingConfirmation={onPrintBookingConfirmation ? handlePrintBookingConfirmation : undefined}
                        />
                    ))}

                    {/* Separator between groups */}
                    {groupIndex < visibleGroups.length - 1 && (
                        <div className="my-1 border-t border-gray-200 dark:border-gray-700"/>
                    )}
                </div>
            ))}
        </div>,
        document.body
    );
};

/**
 * Helper function to build context menu groups based on available handlers
 * Returns a hierarchical structure with parent items containing submenus
 */
export function buildContextMenuGroups(car: Car, config: {
    onFocus?: (car: Car) => void;
    onFocusChassis?: (car: Car) => void;
    onFocusShipper?: (car: Car) => void;
    onFocusConsignee?: (car: Car) => void;
    onEdit?: (car: Car) => void;
    hasChangeVoyage?: boolean;
    hasChangeConsignee?: boolean;
    hasChangeDestination?: boolean;
    hasSetLoaded?: boolean;
    hasSetPricing?: boolean;
    hasSetLengthPricing?: boolean;
    hasInsurance?: boolean;
    hasPrintBL?: boolean;
    hasPrintBookingConfirmation?: boolean;
    hasExportExcel?: boolean;
    hasDelete?: boolean;
}): ContextMenuGroup[] {
    const menuItems: ContextMenuItem[] = [];

    // Focus submenu
    const focusItems: ContextMenuItem[] = [];
    if (config.onFocus) {
        focusItems.push({
            key: 'focus_ref',
            label: `Ref #${car.reference_number}`,
            icon: Icons.focus,
        });
    }
    if (config.onFocusChassis) {
        focusItems.push({
            key: 'focus_chassis',
            label: `Chassis ${car.chassis_number ? (car.chassis_number.length > 12 ? car.chassis_number.substring(0, 12) + '...' : car.chassis_number) : '(None)'}`,
            icon: Icons.focus,
            disabled: !car.chassis_number,
        });
    }
    if (config.onFocusShipper) {
        const shipperLabel = car.shipper?.shortname || car.shipper_name || car.shipper?.name;
        focusItems.push({
            key: 'focus_shipper',
            label: `Shipper ${shipperLabel ? (shipperLabel.length > 15 ? shipperLabel.substring(0, 15) + '...' : shipperLabel) : '(Not Assigned)'}`,
            icon: Icons.focus,
            disabled: !car.shipper_name,
        });
    }
    if (config.onFocusConsignee) {
        focusItems.push({
            key: 'focus_consignee',
            label: `Consignee ${car.consignee_name ? (car.consignee_name.length > 15 ? car.consignee_name.substring(0, 15) + '...' : car.consignee_name) : '(Not Assigned)'}`,
            icon: Icons.focus,
            disabled: !car.consignee_name,
        });
    }
    if (focusItems.length > 0) {
        menuItems.push({
            key: 'focus_ref',
            label: 'Focus',
            icon: Icons.focus,
            items: focusItems,
        });
    }

    // Edit - direct action (no submenu)
    if (config.onEdit) {
        menuItems.push({
            key: 'edit',
            label: 'Edit',
            icon: Icons.edit,
        });
    }

    // Change submenu
    const changeItems: ContextMenuItem[] = [];
    if (config.hasChangeVoyage) {
        changeItems.push({key: 'change_voyage', label: 'Voyage', icon: Icons.voyage, disabled: car.loaded});
    }
    if (config.hasChangeConsignee) {
        changeItems.push({key: 'change_consignee', label: 'Consignee / Shipper', icon: Icons.consignee});
    }
    if (config.hasChangeDestination) {
        changeItems.push({key: 'change_destination', label: 'Destination', icon: Icons.destination});
    }
    if (changeItems.length > 0) {
        menuItems.push({
            key: 'change_voyage',
            label: 'Change',
            icon: Icons.change,
            items: changeItems,
        });
    }

    // Set submenu
    const setItems: ContextMenuItem[] = [];
    if (config.hasSetLoaded) {
        setItems.push({key: 'mark_loaded', label: 'Mark as Loaded', icon: Icons.loaded});
        setItems.push({key: 'mark_unloaded', label: 'Mark as Unloaded', icon: Icons.loaded});
    }
    if (config.hasSetPricing) {
        setItems.push({key: 'set_pricing', label: 'Freight / Collect', icon: Icons.pricing});
    }
    if (config.hasSetLengthPricing) {
        setItems.push({key: 'set_length_pricing', label: 'Price Based on Length', icon: Icons.pricing});
    }
    if (setItems.length > 0) {
        menuItems.push({
            key: 'mark_loaded',
            label: 'Set',
            icon: Icons.set,
            items: setItems,
        });
    }

    // Insurance submenu
    if (config.hasInsurance) {
        menuItems.push({
            key: 'add_insurance',
            label: 'Insurance',
            icon: Icons.insurance,
            items: [
                {key: 'add_insurance', label: 'Add Insurance Fees', icon: Icons.insurance},
                {key: 'remove_insurance', label: 'Remove Insurance Fees', icon: Icons.insurance},
            ],
        });
    }

    // Print submenu
    const printItems: ContextMenuItem[] = [];
    if (config.hasPrintBL) {
        // Bill of Lading - triggers interactive submenu in parent component
        printItems.push({
            key: 'print_bill_of_lading',
            label: 'Bill of Lading',
            icon: Icons.print,
        });
    }
    if (config.hasPrintBookingConfirmation) {
        printItems.push({key: 'print_booking_confirmation', label: 'Booking Confirmation', icon: Icons.print});
    }
    if (printItems.length > 0) {
        menuItems.push({
            key: 'print_bill_of_lading',
            label: 'Print',
            icon: Icons.print,
            items: printItems,
        });
    }

    // Export - direct action (single car export)
    if (config.hasExportExcel) {
        menuItems.push({
            key: 'export_excel',
            label: 'Export to Excel',
            icon: Icons.export,
        });
    }

    // Delete - direct action (no submenu)
    if (config.hasDelete) {
        menuItems.push({
            key: 'delete',
            label: 'Delete',
            icon: Icons.delete,
            danger: true,
        });
    }

    return [{
        title: '',
        items: menuItems
    }];
}

export type {PrintBLOptions, PrintBookingConfirmationOptions};
export default ContextMenu;
